import React, { useEffect, useState } from "react";
import { X, Loader2, Layers, Calendar } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

const money = (v) => `R ${Number(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Consolidate un-invoiced completed jobs for a customer into a single
 * Standalone Invoice (draft). Admin picks a date range → sees a list →
 * ticks the jobs to bundle → hits Consolidate → lands on the new invoice.
 */
export const BulkConsolidateModal = ({ customer, onClose, onCreated }) => {
  const [from_date, setFrom] = useState("");
  const [to_date, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState(
    `${new Date().toLocaleString("en-ZA", { month: "long", year: "numeric" })} — ${customer.name} bulk invoice`,
  );

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (from_date) params.from_date = from_date;
      if (to_date) params.to_date = to_date;
      const res = await adminApi.consolidatableJobs(customer.id, params);
      setRows(Array.isArray(res.jobs) ? res.jobs : []);
      setPicked(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load jobs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = (id) => {
    setPicked((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleAll = () => {
    if (picked.size === rows.length) setPicked(new Set());
    else setPicked(new Set(rows.map((r) => r.id)));
  };

  const totalPicked = rows
    .filter((r) => picked.has(r.id))
    .reduce((sum, r) => sum + Number(r._totals?.gross || 0), 0);

  const create = async () => {
    if (picked.size === 0) return toast.error("Pick at least one job");
    setBusy(true);
    try {
      const inv = await adminApi.consolidateInvoice(customer.id, {
        job_ids: Array.from(picked),
        reference: reference.trim() || null,
      });
      toast.success(`Draft invoice created — ${picked.size} job(s) consolidated`);
      onCreated && onCreated(inv);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not consolidate");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="bulk-consolidate-modal">
      <div className="bg-white rounded-md shadow-xl max-w-3xl w-full p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Bulk consolidate</div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">
              Roll un-invoiced jobs into one invoice
            </h2>
            <p className="text-xs text-[#3f4b52] mt-1">
              For <b>{customer.name}</b> ({customer.code}) — only completed, un-invoiced jobs are shown.
            </p>
          </div>
          <button onClick={onClose} className="text-[#3f4b52]" data-testid="bulk-close-btn" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52]">From date</label>
            <input type="date" value={from_date} onChange={(e) => setFrom(e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="bulk-from-date" />
          </div>
          <div>
            <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52]">To date</label>
            <input type="date" value={to_date} onChange={(e) => setTo(e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="bulk-to-date" />
          </div>
          <div className="flex items-end">
            <button
              type="button" onClick={load} disabled={loading}
              data-testid="bulk-refresh-btn"
              className="w-full h-10 bg-[#20282c] text-white text-xs font-display uppercase tracking-widest rounded-md inline-flex items-center justify-center gap-2 hover:brightness-110"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Calendar size={13} />} Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 border border-[#cfd7db] rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[#f5f6f6] text-[#3f4b52]">
              <tr>
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" checked={picked.size === rows.length && rows.length > 0} onChange={toggleAll} className="accent-[#f28a16]" data-testid="bulk-toggle-all" />
                </th>
                <th className="px-2 py-2 text-left">Job</th>
                <th className="px-2 py-2 text-left">Date</th>
                <th className="px-2 py-2 text-left">Vehicle</th>
                <th className="px-2 py-2 text-left">Client</th>
                <th className="px-2 py-2 text-right">Total incl.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eef2f4]">
              {loading && <tr><td colSpan={6} className="p-6 text-center text-[#3f4b52]">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-[#3f4b52]" data-testid="bulk-empty">
                  No un-invoiced jobs found for this customer in the selected range.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className={picked.has(r.id) ? "bg-[#fff8ea]" : "hover:bg-[#f5f6f6]"} data-testid={`bulk-row-${r.id}`}>
                  <td className="px-2 py-2 text-center">
                    <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} className="accent-[#f28a16]" data-testid={`bulk-pick-${r.id}`} />
                  </td>
                  <td className="px-2 py-2 font-mono">#{r.job_number}</td>
                  <td className="px-2 py-2">{(r.fitment_date || "").slice(0, 10) || "—"}</td>
                  <td className="px-2 py-2">
                    {r.vehicle_make_model || "—"}
                    {r.registration && <span className="ml-1 text-[#3f4b52]">· {r.registration}</span>}
                  </td>
                  <td className="px-2 py-2">{r.client_name || "—"}</td>
                  <td className="px-2 py-2 text-right font-bold">{money(r._totals?.gross)}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-[#f5f6f6]">
                <tr>
                  <td colSpan={5} className="px-2 py-2 text-right font-display uppercase tracking-widest text-xs">
                    Picked {picked.size} of {rows.length} — total
                  </td>
                  <td className="px-2 py-2 text-right font-bold text-[#f28a16]" data-testid="bulk-total">{money(totalPicked)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="mt-4">
          <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52]">Invoice reference / description</label>
          <input value={reference} onChange={(e) => setReference(e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="bulk-reference" />
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]"
            data-testid="bulk-cancel-btn"
          >Cancel</button>
          <button onClick={create} disabled={busy || picked.size === 0} data-testid="bulk-create-btn"
            className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />}
            Consolidate into draft invoice
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkConsolidateModal;
